import { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { articles, siteInfo } from '@/mocks/site';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const article = articles.find((a) => a.id === id);
  const relatedArticles = articles
    .filter((a) => a.id !== id && a.category === article?.category)
    .slice(0, 3);

  if (!article) {
    return (
      <>
        <div className="h-16" />
        <Navbar />
        <main className="min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <h1 className="font-heading font-bold text-2xl text-foreground-950">文章未找到</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    headline: article.title,
    description: article.summary,
    datePublished: article.publishDate,
    author: { '@type': 'Organization', name: siteInfo.name },
    publisher: { '@type': 'Organization', name: siteInfo.name },
    image: article.image,
    url: `https://www.91pian.com/article/${article.id}`,
    inLanguage: 'zh-CN',
    isPartOf: { '@type': 'WebSite', name: siteInfo.name, url: 'https://www.91pian.com' },
  };

  const webPageSchema = {
    name: `${article.title} - 91片`,
    description: article.summary,
    url: `https://www.91pian.com/article/${article.id}`,
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <SchemaOrg type="Article" data={articleSchema} />
      <SchemaOrg type="WebPage" data={webPageSchema} />

      <div className="h-16" />
      <Navbar />

      <main className="min-h-screen bg-background-50">
        <article>
          {/* Hero Image */}
          <div className="w-full h-[350px] md:h-[450px] overflow-hidden bg-background-200">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Content */}
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { name: '首页', href: '/' },
                { name: article.categoryName, href: `/category/${article.category}` },
                { name: article.title },
              ]}
            />

            <div className="mt-6">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                {article.categoryName}
              </span>
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 mt-4 text-sm text-foreground-500">
                <span>发布于 {article.publishDate}</span>
                <span>阅读 {article.readTime}</span>
              </div>
            </div>

            <div
              className="mt-8 prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Article Footer */}
            <div className="mt-12 pt-8 border-t border-background-200">
              <div className="flex flex-wrap gap-2">
                <span className="text-sm text-foreground-600">标签：</span>
                <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs whitespace-nowrap">
                  {article.categoryName}
                </span>
                <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs whitespace-nowrap">
                  91片推荐
                </span>
                <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs whitespace-nowrap">
                  在线观看
                </span>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-12 bg-white">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-50 rounded-lg overflow-hidden hover:bg-background-100 transition-colors"
                  >
                    <div className="aspect-video overflow-hidden bg-background-200">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading font-semibold text-foreground-950 text-sm group-hover:text-primary-500 transition-colors line-clamp-2">
                        {ra.title}
                      </h3>
                      <p className="mt-2 text-xs text-foreground-600 line-clamp-2">{ra.summary}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}